import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { rememberArrivalForAfterSignIn } from './post-auth-return';

/**
 * Where a visitor with no session belongs.
 *
 * Three separate places in the app bounce a sessionless visitor to the login
 * screen, each historically with its own hardcoded route string and its own
 * guest/demo allowlist:
 *
 *   1. top-bar.component.ts   — its authState subscription sees a null user
 *   2. error.interceptor.ts   — a background 401 that a token refresh confirms
 *   3. encryption.service.ts  — localStorage.user has been set to "null"
 *
 * The deferred-login page is a login page (self-contained, no top bar, its own
 * sign-in buttons), so it does not need to be added to those allowlists as a
 * public route would — it needs the redirect *target* to change. Keeping that
 * decision in one place is what stops the three from drifting apart again.
 */
export function sessionlessLandingRoute(): string {
  return environment.features.deferredLogin ? '/auth/start' : '/auth/login';
}

/**
 * Sends a sessionless visitor to the landing route, unless they are already on
 * it.
 *
 * The self-navigation guard is load-bearing. The router is configured app-wide
 * with `onSameUrlNavigation: 'reload'` (app-routing.module.ts), so navigating
 * to the route we are already on tears the component down and builds a new one
 * — which on /auth/start would throw away whatever the visitor had typed into
 * the create form. /auth/login never noticed because it has no state to lose.
 *
 * Checks window.location.hash as well as router.url: during bootstrap router.url
 * is still '/' while the first navigation is in flight, and the 401 path in
 * particular fires that early.
 */
export function goToSessionlessLanding(router: Router): void {
  const target = sessionlessLandingRoute();
  // Before the bounce: once we navigate, the page they came for is gone.
  // Only acts on the page they arrived on — see post-auth-return.ts.
  rememberArrivalForAfterSignIn();

  // With the flag off these three call sites must behave exactly as they did
  // before this helper existed — including the redundant same-URL navigation
  // into /auth/login, which the router turns into a reload. It is almost
  // certainly pointless, but "off changes nothing" is worth more during a
  // rollout than tidying it up here.
  if (environment.features.deferredLogin) {
    const current = (router.url || '').split('?')[0];
    const hash = window.location.hash || '';
    if (current === target || hash.includes(target)) return;
  }

  router.navigateByUrl(target, { replaceUrl: true });
}
